import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../../api/contexto'
import type { ErrorApi } from '../../api/errores'
import type { components } from '../../api/modelos'

export type ClienteDetalle = components['schemas']['ClienteDetalle']
export type Coordenadas = components['schemas']['Coordenadas']
export type ResumenFactura = components['schemas']['ResumenFactura']

/**
 * Consultas propias de la pantalla de logistica. Viven junto a la pantalla y no
 * en `src/hooks/` porque ninguna otra seccion las usa: solo el panel de detalle
 * pide una ficha, geocodifica o consulta la ultima factura.
 */
function claveCliente(id: string) {
  return ['cliente', id] as const
}

export function useClienteDetalle(id: string) {
  const api = useApi()

  return useQuery<ClienteDetalle, ErrorApi>({
    queryKey: claveCliente(id),
    queryFn: ({ signal }) => api.pedir<ClienteDetalle>(`/api/clientes/${id}`, { senal: signal }),
  })
}

export function useGeocodificar(id: string) {
  const api = useApi()
  const consultas = useQueryClient()

  return useMutation<Coordenadas, ErrorApi, void>({
    mutationFn: () =>
      api.pedir<Coordenadas>(`/api/clientes/${id}/geocodificar`, { metodo: 'POST' }),
    // El endpoint persiste las coordenadas en la base de datos, asi que la
    // ficha en cache ha quedado obsoleta. Invalidarla hace que el mapa aparezca
    // solo: en la version Streamlit las coordenadas vivian en memoria de sesion
    // y se perdian en cada recarga.
    onSuccess: () => consultas.invalidateQueries({ queryKey: claveCliente(id) }),
  })
}

export function useUltimaFactura(id: string, activo: boolean) {
  const api = useApi()

  return useQuery<ResumenFactura, ErrorApi>({
    queryKey: ['ultima-factura', id],
    queryFn: ({ signal }) =>
      api.pedir<ResumenFactura>(`/api/clientes/${id}/ultima-factura`, { senal: signal }),
    // Recorre las facturas de Holded y tarda: solo se lanza cuando alguien
    // pulsa el boton, nunca al abrir el panel.
    enabled: activo,
    staleTime: 5 * 60_000,
    // Un 503 significa que Holded no contesta. Reintentar en bucle solo retrasa
    // el mensaje; el reintento aqui lo decide el operador con el boton.
    retry: false,
  })
}
